import {
  Button,
  ButtonMode,
  ButtonSize,
  Checkbox,
  Divider,
  Icon,
  ICONS,
  Popup,
  ActionButton,
} from '@creditsights/styleguide-web';
import { noop } from 'lodash';
import React, { memo, ReactElement, useState } from 'react';
import classNames from 'classnames/bind';
import { RiskDistributionGroupBy } from '../../../../services/dtos/riskProducts/riskDistribution.dto';
import { WIDGET_ID } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import MetricsDropdown from '../MetricsDropdown';
import styles from './ChartActions.module.scss';

const cx = classNames.bind(styles);

interface ChartActionsProps {
  widgetId: WIDGET_ID;
  selectedMetric?: string;
  unratedToggleOn: boolean;
  showUnratedToggleTitle: boolean;
  selectedGroupBy?: RiskDistributionGroupBy;
  onMetricSelect: (value: string) => void;
  onUnratedToggle: (value: boolean) => void;
  onGroupBySelect?: (value: RiskDistributionGroupBy) => void;
  isDownloadDisabled: boolean;
  onDownload: () => Promise<void>;
  dataTestId: string;
}

const ChartActions = ({
  widgetId,
  unratedToggleOn,
  selectedMetric,
  showUnratedToggleTitle,
  selectedGroupBy,
  onMetricSelect,
  onGroupBySelect,
  onUnratedToggle,
  onDownload,
  isDownloadDisabled,
  dataTestId,
}: ChartActionsProps): ReactElement => {
  const [isMenuOpen, setMenuOpen] = useState(false);

  const onGroupByChange = (_, data) => {
    onGroupBySelect?.(data.value);
  };

  const renderDistributionBy = () => {
    if (!selectedGroupBy) {
      return null;
    }
    return (
      <div className={cx('section')}>
        <div className={cx('container')}>
          <span className={cx('title')}>Distribution by:</span>
          <Checkbox
            className={cx('radio-select')}
            radio
            label="Sector"
            name="groupBy"
            value={RiskDistributionGroupBy.SECTOR}
            checked={selectedGroupBy === RiskDistributionGroupBy.SECTOR}
            onChange={onGroupByChange}
            dataTestId={`${dataTestId}-groupby-sector-radio-button`}
            aria-label="Sector"
          />
          <Checkbox
            className={cx('radio-select')}
            radio
            label="ACR"
            name="groupBy"
            value={RiskDistributionGroupBy.RATING}
            checked={selectedGroupBy === RiskDistributionGroupBy.RATING}
            onChange={onGroupByChange}
            dataTestId={`${dataTestId}-groupby-rating-radio-button`}
            aria-label="Rating"
          />
        </div>
        <Divider />
      </div>
    );
  };

  const renderUnratedEntities = () => {
    return (
      <div className={cx('section')}>
        <div className={cx('container')}>
          {showUnratedToggleTitle && <span className={cx('title')}>Display</span>}
          <Checkbox
            aria-label="Unrated Entities"
            checked={unratedToggleOn}
            className={cx('unrated-entities-toggle')}
            dataTestId={`${dataTestId}-unrated-entities-toggle-popup`}
            label="Unrated Entities"
            onClick={() => onUnratedToggle(!unratedToggleOn)}
            toggle
          />
        </div>
        <Divider />
      </div>
    );
  };

  return (
    <div className={cx('body')}>
      <MetricsDropdown
        widgetId={widgetId}
        value={selectedMetric}
        onOptionSelect={(value) => onMetricSelect(value as string)}
        dataTestId={`${dataTestId}-metric-select`}
      />
      <Popup
        data-testid={`${dataTestId}-menu-popup`}
        on="click"
        onClose={() => setMenuOpen(false)}
        onOpen={() => setMenuOpen(true)}
        open={isMenuOpen}
        position="bottom left"
        trigger={
          <ActionButton
            ariaLabel="Menu"
            dark
            dataTestId={`${dataTestId}-menu-trigger`}
            icon={ICONS.MenuDots}
            onClick={noop}
            secondary
            size={12}
            small
            tooltip="Menu"
          />
        }
      >
        <div className={cx('content')}>
          {renderDistributionBy()}
          {renderUnratedEntities()}
          <Button
            ariaLabel="Download"
            dataTestId="change-chart-download"
            className={cx('action')}
            disabled={isDownloadDisabled}
            onClick={onDownload}
            mode={ButtonMode.SECONDARY}
            size={ButtonSize.X_SMALL}
          >
            <Icon name={ICONS.Download} width={12} height={12} />
            Download Image
          </Button>
        </div>
      </Popup>
    </div>
  );
};

export default memo(ChartActions);
